import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import type { CalculatorState, CustomAddon } from "@/lib/calculatorState";

/**
 * Centralized calculator flow state.
 * Lives in memory (React context) — no persistence across full page refreshes.
 * All steps read from and write to this single source of truth.
 */

export interface CalculatorFlowData extends CalculatorState {
  // Step 4 (details) form data
  fullName?: string;
  phone?: string;
  email?: string;
  city?: string;
  address?: string;
  notes?: string;
  callbackRequested?: boolean;

  // Derived pricing data (computed on step 3, consumed on step 4)
  _quoteNumber?: string;
  _adjustedTotal?: number;
  _adjustedSubtotal?: number;
  _adjustedVat?: number;
  _showVat?: boolean;
  _vatRate?: number;
  _isMinimumApplied?: boolean;
  _allLineItems?: any[];
}

interface CalculatorFlowContextType {
  data: CalculatorFlowData;
  /** Merge partial data into the flow state */
  update: (partial: Partial<CalculatorFlowData>) => void;
  /** Reset all flow data (e.g. when starting a new calculation) */
  reset: () => void;
  /** Check if step 1 data is present */
  hasStep1: () => boolean;
  /** Check if step 2 data is present */
  hasStep2: () => boolean;

  /** Deterministic previous-step route for a given current route */
  getPrevRoute: (currentRoute: string) => string;
  /** Deterministic next-step route for a given current route */
  getNextRoute: (currentRoute: string) => string;
}

const CalculatorFlowContext = createContext<CalculatorFlowContextType | null>(null);

// Flow definition: ordered step routes
const STEP_ROUTES_WITH_DIM = [
  "/calculator/measurements/with-dimensions",
  "/calculator/customization",
  "/calculator/summary",
  "/calculator/details",
];

const STEP_ROUTES_NO_DIM = [
  "/calculator/measurements/no-dimensions",
  "/calculator/customization",
  "/calculator/summary",
  "/calculator/details",
];

// Also accept the generic measurements route
const STEP_ROUTES_GENERIC = [
  "/calculator/measurements",
  "/calculator/customization",
  "/calculator/summary",
  "/calculator/details",
];

function getStepRoutes(inputPath?: string): string[] {
  if (inputPath === "no-dimensions") return STEP_ROUTES_NO_DIM;
  if (inputPath === "with-dimensions") return STEP_ROUTES_WITH_DIM;
  return STEP_ROUTES_GENERIC;
}

function normalizeRoute(route: string): string {
  // Strip query/hash
  return route.split("?")[0].split("#")[0].replace(/\/$/, "") || "/";
}

export function CalculatorFlowProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<CalculatorFlowData>({});

  const update = useCallback((partial: Partial<CalculatorFlowData>) => {
    setData((prev) => ({ ...prev, ...partial }));
  }, []);

  const reset = useCallback(() => {
    setData({});
  }, []);

  const hasStep1 = useCallback(() => {
    return Boolean(data.spaceType && data.area && data.area > 0);
  }, [data.spaceType, data.area]);

  const hasStep2 = useCallback(() => {
    return hasStep1() && Boolean(data.finishType);
  }, [data.finishType, hasStep1]);

  const getPrevRoute = useCallback((currentRoute: string) => {
    const norm = normalizeRoute(currentRoute);
    const routes = getStepRoutes(data.inputPath);

    // Find current step index
    let idx = routes.indexOf(norm);

    // Also check across all route sets
    if (idx === -1) {
      for (const r of [STEP_ROUTES_WITH_DIM, STEP_ROUTES_NO_DIM, STEP_ROUTES_GENERIC]) {
        idx = r.indexOf(norm);
        if (idx !== -1) {
          // Use the matched set's prev
          return idx > 0 ? r[idx - 1] : "/";
        }
      }
    }

    if (idx <= 0) return "/";
    return routes[idx - 1];
  }, [data.inputPath]);

  const getNextRoute = useCallback((currentRoute: string) => {
    const norm = normalizeRoute(currentRoute);
    const routes = getStepRoutes(data.inputPath);
    let idx = routes.indexOf(norm);

    if (idx === -1) {
      for (const r of [STEP_ROUTES_WITH_DIM, STEP_ROUTES_NO_DIM, STEP_ROUTES_GENERIC]) {
        idx = r.indexOf(norm);
        if (idx !== -1 && idx < r.length - 1) return r[idx + 1];
      }
    }

    if (idx === -1 || idx >= routes.length - 1) return routes[routes.length - 1];
    return routes[idx + 1];
  }, [data.inputPath]);

  return (
    <CalculatorFlowContext.Provider value={{ data, update, reset, hasStep1, hasStep2, getPrevRoute, getNextRoute }}>
      {children}
    </CalculatorFlowContext.Provider>
  );
}

export function useCalculatorFlow(): CalculatorFlowContextType {
  const ctx = useContext(CalculatorFlowContext);
  if (!ctx) throw new Error("useCalculatorFlow must be inside CalculatorFlowProvider");
  return ctx;
}
